import type {
  MatchTier,
  ScoreBasis,
  SearchHit,
  SearchQuery,
  SearchRequest,
  SearchResponse,
} from "@models/search";

const BASE_URL = import.meta.env.VITE_SEARCH_API_BASE_URL ?? "";

let getAccessToken: (() => Promise<string | null>) | null = null;

export const setSearchAccessTokenFn = (
  fn: () => Promise<string | null>,
): void => {
  getAccessToken = fn;
};

function queryToWire(query: SearchQuery): Record<string, unknown> {
  if (query.type === "image") {
    return { type: "image", image_ref: query.imageRef };
  }
  return { type: "text", text: query.text };
}

function requestToWire(request: SearchRequest): Record<string, unknown> {
  return {
    index_id: request.indexId,
    query: queryToWire(request.query),
    top_k: request.topK,
    filter: request.filter,
  };
}

function hitFromWire(hit: Record<string, unknown>): SearchHit {
  return {
    id: hit.id as string,
    similarity: (hit.similarity as number | null) ?? null,
    rawSimilarity: (hit.raw_similarity as number | null) ?? null,
    scoreBasis: (hit.score_basis as ScoreBasis) ?? undefined,
    indexId: hit.index_id as string,
    title: hit.title as string,
    snippet: (hit.snippet as string) ?? undefined,
    thumbnail: (hit.thumbnail as string) ?? undefined,
    reason: (hit.reason as string) ?? undefined,
    sources: (hit.sources as string[]) ?? undefined,
    metadata: (hit.metadata as Record<string, unknown>) ?? undefined,
  };
}

class SearchApi {
  async search(request: SearchRequest): Promise<SearchResponse> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = getAccessToken ? await getAccessToken() : null;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${BASE_URL}/search`, {
      method: "POST",
      headers,
      body: JSON.stringify(requestToWire(request)),
    });
    if (!response.ok) {
      throw new Error(`Search failed: ${response.status}`);
    }
    const data = (await response.json()) as {
      hits: Record<string, unknown>[];
      match_tier?: MatchTier;
      degraded?: string[] | null;
    };
    return {
      hits: (data.hits ?? []).map(hitFromWire),
      matchTier: data.match_tier,
      degraded: data.degraded ?? [],
    };
  }
}

export const searchApi = new SearchApi();
